import type { Editor } from '@tiptap/core';

/**
 * Which editor the controller is driving, and whether it is still alive.
 *
 * A React callback with an empty dependency list keeps the `editor` from its first render, and
 * entering edit mode gives React a NEW editor. So a callback that runs after a swap and checks the
 * one it kept is looking at an editor that is no longer on the page. Anything that touches the
 * editor after a swap must ask here instead.
 */
let editorRef: Editor | null = null;

export const setLiveEditor = (editor: Editor | null) => {
  editorRef = editor;
};

/** Identity only, destroyed or not - a teardown must be able to name the editor it owns. */
export const isLiveEditor = (editor: Editor): boolean => editorRef === editor;

/** True when the current editor has been destroyed; nothing may read its view or schema then. */
export const liveEditorDestroyed = (): boolean => Boolean(editorRef?.isDestroyed);

/** The editor to act on right now, or null when there is none to act on. */
export const currentEditor = (): Editor | null => (editorRef && !editorRef.isDestroyed ? editorRef : null);

export const liveEditorView = () => currentEditor()?.view ?? null;
